import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { User, Settings, Bell, Shield, Eye, Globe, Palette } from "lucide-react";

interface UserProfile {
  // Personal Information
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  company?: string;
  
  // Avatar/Profile Picture
  avatarUrl?: string;
  avatarBase64?: string;
  
  // System Preferences
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  
  // Notification Preferences
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  weeklyReports: boolean;
  criticalAlerts: boolean;
  maintenanceNotifications: boolean;
  
  // Dashboard & UI Preferences
  defaultDashboard: string;
  itemsPerPage: number;
  showAdvancedFeatures: boolean;
  compactView: boolean;
  
  // Security & Privacy
  twoFactorEnabled: boolean;
  sessionTimeout: number; // in minutes
  ipWhitelist?: string[];
  lastPasswordChange?: string;
  
  // Professional Information
  role: string;
  permissions: string[];
  expertiseAreas: string[];
  certifications: string[];
  
  // Working Preferences
  workingHours: {
    start: string;
    end: string;
    timezone: string;
    workDays: string[];
  };
  
  // Accessibility
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  screenReader: boolean;
}

interface UserProfileProps {
  profile?: UserProfile;
  onSave: (profile: UserProfile) => void;
  onCancel: () => void;
}

const UserProfileManager: React.FC<UserProfileProps> = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState<UserProfile>(profile || {
    firstName: '',
    lastName: '',
    email: '',
    theme: 'system',
    language: 'en',
    timezone: 'America/Los_Angeles',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: '12h',
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyReports: true,
    criticalAlerts: true,
    maintenanceNotifications: true,
    defaultDashboard: 'overview',
    itemsPerPage: 25,
    showAdvancedFeatures: false,
    compactView: false,
    twoFactorEnabled: false,
    sessionTimeout: 480, // 8 hours
    role: 'analyst',
    permissions: [],
    expertiseAreas: [],
    certifications: [],
    workingHours: {
      start: '09:00',
      end: '17:00',
      timezone: 'America/Los_Angeles',
      workDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    },
    highContrast: false,
    fontSize: 'medium',
    reducedMotion: false,
    screenReader: false
  });

  const handleInputChange = (field: keyof UserProfile, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWorkingHoursChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [field]: value
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const roles = [
    'Administrator', 'Database Administrator', 'SQL Developer', 'Data Analyst', 
    'System Architect', 'DevOps Engineer', 'Business Analyst', 'Project Manager'
  ];

  const expertiseOptions = [
    'SQL Server', 'PostgreSQL', 'MySQL', 'Oracle', 'Performance Tuning',
    'Database Design', 'Data Modeling', 'ETL/ELT', 'Cloud Migration',
    'Security & Compliance', 'Backup & Recovery', 'High Availability'
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {formData.avatarBase64 && (
            <img 
              src={formData.avatarBase64} 
              alt="Profile Avatar" 
              className="w-16 h-16 object-cover rounded-full border"
            />
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">User Profile & Preferences</h2>
            <p className="text-gray-600">Manage your personal settings and system preferences</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            form="user-profile-form"
            className="bg-shiftleft-600 hover:bg-shiftleft-700"
          >
            Save Profile
          </Button>
        </div>
      </div>

      <form id="user-profile-form" onSubmit={handleSubmit}>
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="professional">Professional</TabsTrigger>
            <TabsTrigger value="accessibility">Accessibility</TabsTrigger>
          </TabsList>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Personal Information
                </CardTitle>
                <CardDescription>Your basic profile information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="John"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john.doe@company.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Job Title</Label>
                    <Input
                      id="title"
                      value={formData.title || ''}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Senior Database Administrator"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={formData.department || ''}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      placeholder="Information Technology"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={formData.company || ''}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    placeholder="Your Company Name"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Preferences Tab */}
          <TabsContent value="system" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  System Preferences
                </CardTitle>
                <CardDescription>Customize your system experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium">Display Settings</h4>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Theme</Label>
                        <Select 
                          value={formData.theme} 
                          onValueChange={(value: 'light' | 'dark' | 'system') => handleInputChange('theme', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="light">Light</SelectItem>
                            <SelectItem value="dark">Dark</SelectItem>
                            <SelectItem value="system">System Default</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Items per Page</Label>
                        <Select 
                          value={formData.itemsPerPage.toString()} 
                          onValueChange={(value) => handleInputChange('itemsPerPage', parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="25">25</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="compactView">Compact View</Label>
                        <Switch 
                          id="compactView"
                          checked={formData.compactView}
                          onCheckedChange={(checked) => handleInputChange('compactView', checked)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="showAdvanced">Show Advanced Features</Label>
                        <Switch 
                          id="showAdvanced"
                          checked={formData.showAdvancedFeatures}
                          onCheckedChange={(checked) => handleInputChange('showAdvancedFeatures', checked)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Localization</h4>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Language</Label>
                        <Select 
                          value={formData.language} 
                          onValueChange={(value) => handleInputChange('language', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="es">Spanish</SelectItem>
                            <SelectItem value="fr">French</SelectItem>
                            <SelectItem value="de">German</SelectItem>
                            <SelectItem value="zh">Chinese</SelectItem>
                            <SelectItem value="ja">Japanese</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Select 
                          value={formData.timezone} 
                          onValueChange={(value) => handleInputChange('timezone', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/New_York">Eastern Time</SelectItem>
                            <SelectItem value="America/Chicago">Central Time</SelectItem>
                            <SelectItem value="America/Denver">Mountain Time</SelectItem>
                            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                            <SelectItem value="UTC">UTC</SelectItem>
                            <SelectItem value="Europe/London">London</SelectItem>
                            <SelectItem value="Europe/Paris">Paris</SelectItem>
                            <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Date Format</Label>
                        <Select 
                          value={formData.dateFormat} 
                          onValueChange={(value) => handleInputChange('dateFormat', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MM/dd/yyyy">MM/DD/YYYY</SelectItem>
                            <SelectItem value="dd/MM/yyyy">DD/MM/YYYY</SelectItem>
                            <SelectItem value="yyyy-MM-dd">YYYY-MM-DD</SelectItem>
                            <SelectItem value="MMM dd, yyyy">MMM DD, YYYY</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Time Format</Label>
                        <Select 
                          value={formData.timeFormat} 
                          onValueChange={(value: '12h' | '24h') => handleInputChange('timeFormat', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12h">12 Hour (AM/PM)</SelectItem>
                            <SelectItem value="24h">24 Hour</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Preferences
                </CardTitle>
                <CardDescription>Control how and when you receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Notification Channels</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="emailNotif">Email Notifications</Label>
                        <p className="text-sm text-gray-500">Receive notifications via email</p>
                      </div>
                      <Switch 
                        id="emailNotif"
                        checked={formData.emailNotifications}
                        onCheckedChange={(checked) => handleInputChange('emailNotifications', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="pushNotif">Push Notifications</Label>
                        <p className="text-sm text-gray-500">Browser push notifications</p>
                      </div>
                      <Switch 
                        id="pushNotif"
                        checked={formData.pushNotifications}
                        onCheckedChange={(checked) => handleInputChange('pushNotifications', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="smsNotif">SMS Notifications</Label>
                        <p className="text-sm text-gray-500">Text message alerts for critical issues</p>
                      </div>
                      <Switch 
                        id="smsNotif"
                        checked={formData.smsNotifications}
                        onCheckedChange={(checked) => handleInputChange('smsNotifications', checked)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Notification Types</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="criticalAlerts">Critical Alerts</Label>
                        <p className="text-sm text-gray-500">System failures and critical issues</p>
                      </div>
                      <Switch 
                        id="criticalAlerts"
                        checked={formData.criticalAlerts}
                        onCheckedChange={(checked) => handleInputChange('criticalAlerts', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="weeklyReports">Weekly Reports</Label>
                        <p className="text-sm text-gray-500">Weekly system health summaries</p>
                      </div>
                      <Switch 
                        id="weeklyReports"
                        checked={formData.weeklyReports}
                        onCheckedChange={(checked) => handleInputChange('weeklyReports', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="maintenanceNotif">Maintenance Notifications</Label>
                        <p className="text-sm text-gray-500">Scheduled maintenance windows</p>
                      </div>
                      <Switch 
                        id="maintenanceNotif"
                        checked={formData.maintenanceNotifications}
                        onCheckedChange={(checked) => handleInputChange('maintenanceNotifications', checked)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security & Privacy
                </CardTitle>
                <CardDescription>Manage your security settings and privacy preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="twoFactor">Two-Factor Authentication</Label>
                      <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                    </div>
                    <Switch 
                      id="twoFactor"
                      checked={formData.twoFactorEnabled}
                      onCheckedChange={(checked) => handleInputChange('twoFactorEnabled', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Session Timeout (minutes)</Label>
                    <Select 
                      value={formData.sessionTimeout.toString()} 
                      onValueChange={(value) => handleInputChange('sessionTimeout', parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                        <SelectItem value="480">8 hours</SelectItem>
                        <SelectItem value="1440">24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Professional Tab */}
          <TabsContent value="professional" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Professional Information</CardTitle>
                <CardDescription>Your role, expertise, and working preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(value) => handleInputChange('role', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role} value={role.toLowerCase().replace(/\s+/g, '-')}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Areas of Expertise</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {expertiseOptions.map(expertise => (
                      <label key={expertise} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.expertiseAreas.includes(expertise)}
                          onChange={(e) => {
                            const current = formData.expertiseAreas;
                            if (e.target.checked) {
                              handleInputChange('expertiseAreas', [...current, expertise]);
                            } else {
                              handleInputChange('expertiseAreas', current.filter(e => e !== expertise));
                            }
                          }}
                          className="rounded border-gray-300"
                          title={`Select ${expertise} as area of expertise`}
                        />
                        <span className="text-sm">{expertise}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Working Hours</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={formData.workingHours.start}
                        onChange={(e) => handleWorkingHoursChange('start', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={formData.workingHours.end}
                        onChange={(e) => handleWorkingHoursChange('end', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Timezone</Label>
                      <Select 
                        value={formData.workingHours.timezone} 
                        onValueChange={(value) => handleWorkingHoursChange('timezone', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/New_York">Eastern Time</SelectItem>
                          <SelectItem value="America/Chicago">Central Time</SelectItem>
                          <SelectItem value="America/Denver">Mountain Time</SelectItem>
                          <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Working Days</Label>
                    <div className="flex gap-2 flex-wrap">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <label key={day} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.workingHours.workDays.includes(day)}
                            onChange={(e) => {
                              const current = formData.workingHours.workDays;
                              if (e.target.checked) {
                                handleWorkingHoursChange('workDays', [...current, day]);
                              } else {
                                handleWorkingHoursChange('workDays', current.filter(d => d !== day));
                              }
                            }}
                            className="rounded border-gray-300"
                            title={`Select ${day} as working day`}
                          />
                          <span className="text-sm capitalize">{day.substr(0, 3)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Accessibility Tab */}
          <TabsContent value="accessibility" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Accessibility Settings
                </CardTitle>
                <CardDescription>Customize the interface for better accessibility</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="highContrast">High Contrast Mode</Label>
                      <p className="text-sm text-gray-500">Increase contrast for better visibility</p>
                    </div>
                    <Switch 
                      id="highContrast"
                      checked={formData.highContrast}
                      onCheckedChange={(checked) => handleInputChange('highContrast', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Font Size</Label>
                    <Select 
                      value={formData.fontSize} 
                      onValueChange={(value: 'small' | 'medium' | 'large') => handleInputChange('fontSize', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="reducedMotion">Reduced Motion</Label>
                      <p className="text-sm text-gray-500">Minimize animations and transitions</p>
                    </div>
                    <Switch 
                      id="reducedMotion"
                      checked={formData.reducedMotion}
                      onCheckedChange={(checked) => handleInputChange('reducedMotion', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="screenReader">Screen Reader Support</Label>
                      <p className="text-sm text-gray-500">Optimize interface for screen readers</p>
                    </div>
                    <Switch 
                      id="screenReader"
                      checked={formData.screenReader}
                      onCheckedChange={(checked) => handleInputChange('screenReader', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
};

export default UserProfileManager;
